import { BEFORE_REACTIONS, WAVE_COMMENTS, WAVE_REACTIONS } from '@/content/product';
import { NEVERZERO_PRICE_AMOUNT, NEVERZERO_PRICE_LABEL } from '@/lib/pricing';

import type { IconName } from '@/components/Icon';
import type { PostingFrequency } from '@/lib/call';

export type Lang = 'en' | 'fr';

export const LANGS: Lang[] = ['en', 'fr'];
export const DEFAULT_LANG: Lang = 'en';

export interface FocusItem {
  title: string;
  body: string;
  /**
   * L'icône est déclarée par langue parce qu'elle appartient à l'item : si
   * quelqu'un réordonne les quatre cartes d'un seul côté, le typage l'oblige
   * quand même à dire laquelle va avec laquelle, là où un tableau d'icônes
   * indexé à part se serait décalé en silence.
   */
  icon: IconName;
}

export interface Step {
  n: string;
  title: string;
  body: string;
}

/** Une ligne du comparatif : NeverZero, pods, likes achetés, ne rien faire — dans cet ordre. */
export interface CompareRow {
  label: string;
  cells: [string, string, string, string];
}

export interface FaqGroup {
  title: string;
  items: Faq[];
}

export interface Faq {
  q: string;
  a: string;
  /** Rang sur la landing (1 = en premier). Absent : la question ne vit que sur `/faq`. */
  top?: number;
}

/**
 * La page `/white-label` : une agence revend NeverZero à ses clients. Tout ce
 * qu'elle dit est vérifié dans le provisioning (`neverzero-provisioning.service.ts`) :
 * un même email regroupe tous les profils dans un compte, un abonnement par
 * profil, aucun email au propriétaire du profil (on n'a pas le sien), numéro de
 * TVA collecté au checkout, profil déjà rattaché à un autre compte refusé.
 *
 * ⛔ Pas de remise revendeur, pas d'account manager, pas d'onboarding, pas de
 * rapport : rien de tout ça n'existe. Un prix revendeur est une décision de
 * Stephen.
 */
export interface WhiteLabelCopy {
  navLabel: string;
  seoTitle: string;
  seoDescription: string;
  badge: string;
  h1a: string;
  h1b: string;
  lead: string;
  checks: string[];
  splitAgencyTitle: string;
  splitAgency: string[];
  splitClientTitle: string;
  splitClient: string[];
  howEyebrow: string;
  howH2: string;
  steps: Step[];
  invisibleEyebrow: string;
  invisibleH2: string;
  invisibleP: string;
  invisibleItems: FocusItem[];
  pricingEyebrow: string;
  pricingH2: string;
  pricingPer: string;
  pricingItems: string[];
  pricingMargin: string;
  volumeTitle: string;
  volumeBody: string;
  faqEyebrow: string;
  faqH2: string;
  faqs: Faq[];
  ctaH2: string;
  ctaP: string;
}

export interface Dict {
  navServices: string;
  navMethod: string;
  navTeam: string;
  navPricing: string;
  navBlog: string;
  navTools: string;
  protect: string;
  callCta: string;

  heroBadge: string;
  heroH1a: string;
  heroH1b: string;
  heroP1: string;
  redirecting: string;
  urlInvalid: string;
  checkoutFailed: string;

  formSubmit: string;

  noAccess: string;
  realPeople: string;
  guarantee: string;
  noCommitment: string;

  servicesEyebrow: string;
  services: FocusItem[];

  journeyH2: string;
  journeyP: string;
  journeySteps: { title: string; body: string }[];
  journeyImageAlt: string;

  previewToast: string;
  previewToastTime: string;
  previewProgress: string;
  previewReactions: string;
  previewComments: string;
  previewNote: string;

  quote: string;
  quoteAuthor: string;

  trustedBy: string;
  clientsTrust: string;

  focusEyebrow: string;
  focusH2: string;
  focusP: string;
  focusItems: FocusItem[];

  promiseH2a: string;
  promiseH2b: string;
  promiseP: string;

  compareEyebrow: string;
  compareH2: string;
  compareCols: [string, string, string, string];
  compareRows: CompareRow[];

  howEyebrow: string;
  howH2: string;
  steps: Step[];
  /** Libellé accessible du lecteur de la vidéo « Our method » (section #method). */
  methodVideoLabel: string;

  riskEyebrow: string;
  riskH2: string;
  riskP: string;
  riskItems: FocusItem[];

  trustEyebrow: string;
  trustH2: string;
  trustP: string;
  trustItems: string[];

  teamEyebrow: string;
  joinH2: string;
  joinP: string;
  joinCount: string;

  testiEyebrow: string;
  testiH2: string;
  testiBased: string;
  testiStars: string;

  pricingEyebrow: string;
  pricingH2: string;
  pricingPer: string;
  pricingItems: string[];
  pricingMath: string;

  faqH2: string;
  faqSeeAll: string;
  faqPageTitle: string;
  faqPageIntro: string;
  faqGroups: FaqGroup[];

  /** Titre de la page d'accueil (onglet, partage) : le slogan, indépendant du bloc CTA. */
  seoTitle: string;
  ctaH2: string;
  ctaP: string;
  ctaNote: string;

  privacy: string;
  terms: string;
  extensionsPrivacy: string;
  extensionsTerms: string;
  footerTag: string;

  blogTitle: string;
  blogIntro: string;
  blogBack: string;
  blogEmpty: string;
  blogReadMore: string;
  blogMinutes: string;
  blogCtaH2: string;
  blogCtaP: string;

  toolsPageTitle: string;
  toolsPageIntro: string;
  toolsEmpty: string;
  toolsTry: string;

  welcomeTitle: string;
  welcomeLead: string;
  welcomeSteps: string[];
  welcomeMailNote: string;
  welcomeBack: string;

  /** Page `/call` : réserver un appel avec Stephen, ou demander à être rappelé. */
  callEyebrow: string;
  callTitle: string;
  callLead: string;
  callDuration: string;
  callCoversTitle: string;
  callCovers: string[];
  callSkip: string;
  callSkipLink: string;
  callPickTitle: string;
  callLoading: string;
  callTimeZone: string;
  callNoTimeWorks: string;
  callChangeTime: string;
  callBackToTimes: string;
  callName: string;
  callEmail: string;
  callLinkedIn: string;
  callOptional: string;
  callFrequency: string;
  callFrequencyOptions: Record<PostingFrequency, string>;
  callNote: string;
  callNotePlaceholder: string;
  callBookSubmit: string;
  callSending: string;
  callErrorTaken: string;
  callErrorInvalid: string;
  callErrorFailed: string;
  callBookedTitle: string;
  callBookedLead: string;
  callCallbackTitle: string;
  callCallbackLead: string;
  callCallbackOnlyLead: string;
  callPhone: string;
  callCallbackNotePlaceholder: string;
  callCallbackSubmit: string;
  callCallbackSentTitle: string;
  callCallbackSentLead: string;

  notFoundTitle: string;
  notFoundBody: string;
  whiteLabel: WhiteLabelCopy;
  notFoundBack: string;
}

/**
 * ⚠️ Message produit corrigé par rapport à la maquette Claude Design : NeverZero
 * booste TOUS les posts, pas seulement les posts business. La maquette disait
 * l'inverse en six endroits (hero, section Focus, étape 2, liste « Trust »,
 * FAQ, CTA) — tout est réécrit ici. Ne pas recopier les formulations de
 * `NeverZero Landing.dc.html`, elles sont périmées.
 *
 * ⚠️ Les deux langues ne décrivent PAS le même produit et ce n'est pas un oubli
 * de traduction : le pool anglais est exclusivement américain, le pool français
 * exclusivement français. Plusieurs clés le disent explicitement dans chaque
 * langue dans trois réponses de `faqGroups` : « What kind of people… »,
 * « Are the profiles based in the US? », « Will this look like fake engagement? ».
 * Le hero (`realPeople`), l'étape 3, la liste « Trust » et le comparatif le
 * disent aussi.
 *
 * ⚠️ Le code ne l'impose PAS (`user-pool.service.ts`, `resolveEngagementMarket` :
 * « core » couvre ~28 pays). ✅ DÉCISION DE STEPHEN, 23/09/2026 : on garde le
 * claim « profils US / français uniquement » (#719 clos en ce sens). Ne pas le
 * retirer sans lui.
 * Ne pas « harmoniser » : un acheteur français à qui on promet des profils
 * américains reçoit un engagement qui n'intéresse pas son réseau, et
 * inversement.
 */
const en: Dict = {
  navServices: 'Services',
  navMethod: 'Method',
  navTeam: 'Team',
  navPricing: 'Pricing',
  navBlog: 'Blog',
  navTools: 'Free tools',
  protect: 'Work with us',
  callCta: 'Talk to the team',

  heroBadge: 'LinkedIn growth agency',
  heroH1a: 'We make sure your posts',
  heroH1b: 'never land at zero.',
  heroP1:
    'NeverZero is a LinkedIn agency founded by Stephen and Tom. You keep posting as usual. Our team brings real people to every post you publish.',
  redirecting: 'Taking you to secure checkout…',
  urlInvalid: 'That doesn’t look like a LinkedIn profile URL. Try linkedin.com/in/your-profile.',
  checkoutFailed: 'We couldn’t open the checkout. Please try again in a moment.',

  formSubmit: 'Work with us',

  noAccess: 'No password',
  realPeople: 'Real people, US profiles only',
  guarantee: 'Refunded within 30 days',
  noCommitment: 'Cancel anytime',

  servicesEyebrow: 'What we handle for you',
  services: [
    {
      title: 'We watch your profile',
      body: 'When you publish, we spot the new post on our own. Nothing to send us, nothing to schedule.',
      icon: 'eye',
    },
    {
      title: 'Real people show up',
      body: `Up to ${WAVE_REACTIONS} reactions and ${WAVE_COMMENTS} comments on each post, from real US-based LinkedIn members.`,
      icon: 'users',
    },
    {
      title: 'Paced like real attention',
      body: 'Reactions arrive over a few hours, never in one spike. Your account is never touched.',
      icon: 'clock',
    },
  ],

  journeyH2: 'From a post to an opportunity',
  journeyP:
    'Each step feeds the next: a post that starts above zero gets read, gets answered, and reaches the people you wrote it for.',
  journeySteps: [
    { title: 'Credibility', body: 'A post that already has reactions looks worth reading.' },
    { title: 'Reach', body: 'Early engagement gives LinkedIn a reason to show it further.' },
    { title: 'Conversations', body: 'Comments written for your post invite others to join in.' },
    { title: 'Opportunities', body: 'Your launches, hires and wins reach the people they’re meant for.' },
  ],
  journeyImageAlt: 'Two people working together on a laptop',

  previewToast: 'New post spotted, boost started',
  previewToastTime: 'Just now',
  previewProgress: 'Boost in progress',
  previewReactions: 'Reactions',
  previewComments: 'Comments',
  previewNote: 'Real people, over a few hours.',

  quote:
    '“LinkedIn is the best place to build in public. Our job is to make sure none of your posts get ignored.”',
  quoteAuthor: 'The NeverZero team',

  trustedBy: 'Works alongside the tools you already use',
  clientsTrust: 'They trust us',

  focusEyebrow: 'Business posts first, every post covered',
  focusH2: 'Every post starts above zero.',
  focusP:
    `A launch stuck at ${BEFORE_REACTIONS} likes doesn’t just miss reach. It tells everyone scrolling past that nobody cared. Launches, hiring posts and customer wins are the posts that pay your bills, and the ones that can’t afford a flat start. NeverZero covers every post you publish, so those never land in the void.`,
  focusItems: [
    { title: 'Product launches', body: '“Get the attention your launch deserves.”', icon: 'rocket' },
    { title: 'Customer wins', body: '“Turn your success into more opportunities.”', icon: 'trophy' },
    { title: 'Hiring', body: '“Reach the right talent, faster.”', icon: 'user-plus' },
    { title: 'And everything else', body: '“Hot takes, updates, personal stories. All of it starts above zero.”', icon: 'sparkles' },
  ],

  promiseH2a: 'We don’t promise virality.',
  promiseH2b: 'We make sure you don’t start at zero.',
  promiseP:
    'When someone lands on your post, they don’t find a desert. The first reactions and comments are already there. That’s the whole job. Then we step back and let your content do the rest.',

  compareEyebrow: 'Compared with the alternatives',
  compareH2: 'Why not a pod, or bought likes?',
  compareCols: ['NeverZero', 'Engagement pods', 'Bought likes', 'Doing nothing'],
  compareRows: [
    { label: 'Your time', cells: ['None, publish as usual', 'Engaging with others, every day', 'None', 'None'] },
    { label: 'Who engages', cells: ['Real US-based LinkedIn members', 'Group members, whatever their field', 'Often bots or empty profiles', 'Whoever happens to see it'] },
    { label: 'Comments', cells: ['Written for your post, in its language', 'Often generic', 'Rarely', 'Often none'] },
    { label: 'How it arrives', cells: ['Spread over a few hours', 'Whenever the group shows up', 'Often in one spike', 'Often it doesn’t'] },
    { label: 'Cost', cells: [`${NEVERZERO_PRICE_LABEL}/month excl. VAT`, 'Free, paid in time', 'Cheap per like', 'Free'] },
  ],

  howEyebrow: 'Our method',
  howH2: 'You publish. We handle the rest.',
  methodVideoLabel:
    'Our method in 30 seconds: you send us your LinkedIn profile, we take it from there, and every post gets its audience.',
  steps: [
    {
      n: '1',
      title: 'You send us your LinkedIn profile',
      body: 'Just the public URL, then checkout. We never ask for a password.',
    },
    {
      n: '2',
      title: 'We take it from there',
      body: 'Your profile is set up on our side. You keep posting as usual.',
    },
    {
      n: '3',
      title: 'Every post gets its audience',
      body: `Our network brings up to ${WAVE_REACTIONS} reactions and ${WAVE_COMMENTS} comments from real US-based members.`,
    },
  ],

  riskEyebrow: 'Zero risk',
  riskH2: 'Not happy? Refunded.',
  riskP: 'Nothing to lose.',
  riskItems: [
    { title: 'Refunded within 30 days', body: 'Reply to your receipt. We refund your first month.', icon: 'rotate-ccw' },
    { title: 'No password', body: 'We never touch your account.', icon: 'lock' },
    { title: 'Cancel anytime', body: 'Monthly. No commitment.', icon: 'calendar-x' },
  ],

  trustEyebrow: 'Built for trust',
  trustH2: 'Your account stays untouched.',
  trustP:
    'Can LinkedIn penalize my account? It’s the question everyone asks. We never log into it and nothing runs on your side. No password, no extension, no automation. All we use is your public profile URL, and the reactions and comments come from other members’ profiles.',
  trustItems: [
    'No access to your account, nothing runs on it',
    'No password, no extension to install',
    'Real LinkedIn members, US-based profiles only',
    'Small waves over a few hours, never spikes',
    'Nothing added to your posts or your profile',
    'Monthly, cancel anytime',
    'Not satisfied in your first 30 days? Full refund',
  ],

  teamEyebrow: 'The team',
  joinH2: 'The people behind your posts.',
  joinP:
    'We built NeverZero because we had the same problem. Our personal stories got attention. Our product launches, hiring posts and company updates often didn’t. So we built a simple safety net for every post that matters.',
  joinCount: 'LinkedIn professionals in the community behind every boost',

  testiEyebrow: 'What founders say',
  testiH2: 'In their own words.',
  testiBased: 'based on {count} founder reviews',
  testiStars: 'Rated {count} out of 5',

  pricingEyebrow: 'Pricing',
  pricingH2: 'One monthly retainer. Every post covered.',
  pricingPer: '/month excl. VAT',
  pricingItems: [
    'Every original post you publish, up to 100 a month',
    `Each one gets up to ${WAVE_REACTIONS} reactions and ${WAVE_COMMENTS} comments`,
    'No setup fee, no minimum term',
    'Cancel anytime from the link in your receipt',
    'Satisfied or refunded, with a full refund within 30 days of your first payment',
  ],
  // Trois posts par semaine ≈ 13 posts par mois.
  pricingMath: `Posting three times a week? That’s about €${Math.round(NEVERZERO_PRICE_AMOUNT / 13)} per post.`,

  faqH2: 'Common questions.',
  faqSeeAll: 'See all {count} questions',
  faqPageTitle: 'Everything you might want to know.',
  faqPageIntro:
    'How NeverZero works, who engages with your posts, which posts are covered, and what happens when you cancel.',
  faqGroups: [
    {
      title: 'How NeverZero works',
      items: [
        {
          top: 1,
          q: 'What exactly does NeverZero do?',
          a: `It gives every LinkedIn post you publish a first wave of engagement (up to ${WAVE_REACTIONS} reactions and ${WAVE_COMMENTS} comments) so it never starts at zero. You give us your LinkedIn profile, keep publishing as usual, and a member of our team handles the rest.`,
        },
        {
          q: 'How does NeverZero detect my new posts?',
          a: 'We check your public LinkedIn profile for new posts, every few minutes during the working day and at least every two hours outside it. You never have to submit a post.',
        },
        {
          q: 'Do I need to install anything?',
          a: 'No. No browser extension, no desktop app, no mobile app.',
        },
        {
          top: 4,
          q: 'Do you need access to my LinkedIn account?',
          a: 'No. We only need your public LinkedIn profile URL. No password, no login, no connection to your account. Nothing ever runs on it.',
        },
        {
          q: 'Do I have to send you every post?',
          a: 'No. Once your subscription is active, every new post on your profile is picked up automatically.',
        },
        {
          q: 'What happens after I publish?',
          a: 'As soon as we detect the post, its first wave starts: a handful of reactions in the first minutes, then the rest spread over the next few hours, with the comments in between.',
        },
        {
          top: 6,
          q: 'How quickly does engagement start?',
          a: 'As soon as we detect your post. Most of the time that is within minutes of it going live; at night and on weekends it can take up to about two hours.',
        },
        {
          q: 'When does NeverZero stop?',
          a: 'Once the first wave has been delivered, usually within a few hours of publishing. We don’t keep adding engagement to a post after that.',
        },
      ],
    },
    {
      title: 'Who engages with my posts?',
      items: [
        {
          top: 5,
          q: 'What kind of people interact with my posts?',
          a: 'Founders, operators and professionals in your ecosystem: people who would plausibly engage with your content anyway. On the English-speaking side, our network is United States only: every profile that engages with your posts is US-based.',
        },
        {
          q: 'Are these real LinkedIn profiles?',
          a: 'Yes. Every reaction and comment comes from the profile of a real LinkedIn member in our network. No fake or generated profiles.',
        },
        {
          q: 'Are the profiles based in the US?',
          a: 'Yes. On the English-speaking side, every profile that engages with your posts is based in the United States.',
        },
        {
          q: 'Can I choose who engages with my posts?',
          a: 'No. You don’t pick profiles: matching is automatic, so there is nothing to manage.',
        },
        {
          q: 'Can I choose industries or job titles?',
          a: 'No. Profiles are matched to the topic of your posts automatically, but we don’t offer targeting by industry or job title.',
        },
        {
          q: 'Will the same people engage with every post?',
          a: 'No fixed group. Each wave is drawn from our network with a random factor, so the profiles vary from one post to the next.',
        },
        {
          q: 'What kind of reactions can I receive?',
          a: 'A mix of LinkedIn reactions (Like, Insightful, Celebrate, Support and Love) adapted to the tone of the post, plus comments. A difficult announcement won’t get a Celebrate.',
        },
        {
          q: 'Are the comments relevant to my post?',
          a: 'Yes. Each comment is written for your post, from what it actually says, in the language it is written in, not a generic one-liner. A post with no text at all gets reactions only, since there is nothing to comment on.',
        },
      ],
    },
    {
      title: 'Will it look natural?',
      items: [
        {
          top: 3,
          q: 'Will this look like fake engagement?',
          a: 'No. Reactions and comments come from real, relevant US LinkedIn profiles and arrive gradually over a few hours, in small volumes. A natural first wave, not a spike.',
        },
        {
          top: 2,
          q: 'Can LinkedIn penalize my account?',
          a: 'We never log into your account and nothing runs on it: no password, no extension, no automation on your side. All we use is your public profile URL. The reactions and comments come from other members’ profiles, in small waves spread over a few hours rather than a sudden spike.',
        },
        {
          q: 'Do you send hundreds of likes at once?',
          a: `No. Up to ${WAVE_REACTIONS} reactions per post, spread over several hours. A sudden spike is exactly what we avoid.`,
        },
        {
          q: 'Will every post get exactly the same amount of engagement?',
          a: 'No. The size of the wave varies a little from post to post, and so does the mix of reactions.',
        },
        {
          top: 10,
          q: 'Do you guarantee a specific number of likes or comments?',
          a: `Each wave is sized up to ${WAVE_REACTIONS} reactions and ${WAVE_COMMENTS} comments, but it is a first wave, not a counter: the exact figures vary from post to post, and we don’t promise an identical count every time.`,
        },
        {
          q: 'Do you guarantee more impressions or reach?',
          a: 'No. We deliver the first engagement. What LinkedIn does with your post afterwards (impressions, reach, leads, sales) depends on your content and your audience, and nobody can guarantee it.',
        },
        {
          q: 'Can NeverZero make a post go viral?',
          a: 'That is not the promise. We make sure your post doesn’t start at zero. What happens next depends on the post.',
        },
        {
          q: 'Can people tell I’m using NeverZero?',
          a: 'Nothing is added to your posts or your profile: no badge, no label, no message. All anyone sees is people reacting and commenting.',
        },
      ],
    },
    {
      title: 'Which posts are covered?',
      items: [
        {
          top: 8,
          q: 'Which of my posts are covered?',
          a: 'Every original post you publish while your subscription is active: launches, hiring, customer wins, case studies, opinions, personal stories. Reposts of other people’s content are not boosted, and neither are posts published before you subscribed.',
        },
        {
          q: 'Is there a limit on how many posts are covered?',
          a: 'Up to 100 posts a month, with no daily limit. Far more than anyone publishes.',
        },
        {
          q: 'Can I exclude a post?',
          a: 'Yes, but not from a button: there is no dashboard to manage. Send us the link to the post, using the contact details on your receipt, and we’ll take it out of the queue. Posts are picked up quickly, so tell us as soon as it is published.',
        },
        {
          q: 'What if I publish several times in one day?',
          a: 'Each post is detected and gets its own first wave. There is no daily limit.',
        },
        {
          q: 'Does NeverZero work with text posts?',
          a: 'Yes.',
        },
        {
          q: 'Does it work with image, carousel and video posts?',
          a: 'Yes. Any original post on your profile is covered, whatever its format. A post with no text at all gets reactions but no comments.',
        },
        {
          q: 'Does it work with LinkedIn newsletters or articles?',
          a: 'NeverZero is built for regular feed posts. LinkedIn articles and newsletters are not guaranteed to be picked up.',
        },
      ],
    },
    {
      title: 'Account, privacy and setup',
      items: [
        {
          q: 'Do you need my LinkedIn password?',
          a: 'No. Never.',
        },
        {
          q: 'Can NeverZero post from my account?',
          a: 'No. We have no access to your account, so we can’t publish anything from it.',
        },
        {
          q: 'Can NeverZero send messages from my account?',
          a: 'No. Same reason: no access, no messages.',
        },
        {
          q: 'Can NeverZero edit or delete my posts?',
          a: 'No. We can’t touch your posts. Reactions and comments come from other profiles.',
        },
        {
          q: 'Do I need to give you admin access to anything?',
          a: 'No. Your public LinkedIn profile URL is all we need.',
        },
        {
          q: 'Can I use NeverZero on a company page?',
          a: 'Not today. NeverZero works on personal LinkedIn profiles only: sign-up takes a linkedin.com/in/ profile URL, not a company page.',
        },
      ],
    },
    {
      title: 'Pricing, cancellation and results',
      items: [
        {
          q: 'How much does NeverZero cost?',
          a: `${NEVERZERO_PRICE_LABEL} per month, excluding VAT, for every post you publish. No setup fee.`,
        },
        {
          top: 7,
          q: 'What if I’m not satisfied?',
          a: 'You get your money back. If NeverZero isn’t for you, ask within 30 days of your first payment by replying to your receipt, and we refund that first month in full. No questions asked.',
        },
        {
          q: 'Is there a long-term commitment?',
          a: 'No. It is a monthly subscription with no minimum term. Cancel anytime from the link in your receipt.',
        },
        {
          top: 9,
          q: 'I only post a few times a month. Is it worth it?',
          a: `The price is the same whether you post twice or twenty times: ${NEVERZERO_PRICE_LABEL} a month. If you post once or twice a month, that is around €100 or more per post, and it is probably not worth it. From about once a week, every post that matters (a launch, a hire, a customer win) starts with an audience instead of silence.`,
        },
        {
          q: 'What happens if I cancel?',
          a: 'Your posts stay covered until the end of the month you have paid for, then we stop. Posts that already got their first wave keep their reactions and comments.',
        },
        {
          q: 'Who is NeverZero for?',
          a: 'Founders, operators, consultants and professionals who publish on LinkedIn for their business and don’t want their important posts to start at zero.',
        },
      ],
    },
  ],

  seoTitle: 'LinkedIn growth agency. Never post to zero again.',
  ctaH2: 'Send us your profile. We take it from there.',
  ctaP: 'Our team covers every post you publish, starting with your next one.',
  ctaNote: `${NEVERZERO_PRICE_LABEL}/month excl. VAT · Refunded within 30 days · Cancel anytime`,

  privacy: 'Privacy',
  terms: 'Terms',
  extensionsPrivacy: 'Browser extensions privacy',
  extensionsTerms: 'Browser extensions terms',
  footerTag: 'Built for a more open professional world.',

  blogTitle: 'The NeverZero blog',
  blogIntro: 'Notes on LinkedIn reach, distribution and building in public, written for founders.',
  blogBack: '← All articles',
  blogEmpty: 'No articles yet. Come back soon.',
  blogReadMore: 'Read the article',
  blogMinutes: 'min read',
  blogCtaH2: 'Never post to zero again.',
  blogCtaP: 'Give every post you publish the first wave of engagement it needs.',

  toolsPageTitle: 'Free LinkedIn tools',
  toolsPageIntro: 'Quick, no-login diagnostics for your LinkedIn posts.',
  toolsEmpty: 'No tool in this language yet. Check back soon.',
  toolsTry: 'Try the tool',

  welcomeTitle: 'You’re in.',
  welcomeLead:
    'Your subscription is active. A member of our team takes it from here: from now on, every post you publish on LinkedIn gets its first wave of engagement.',
  welcomeSteps: [
    'Nothing to install, nothing to connect: we already have your public LinkedIn URL.',
    'Your next post is picked up on its own, usually within minutes of going live.',
    'Your receipt carries the link to manage or cancel your subscription. Keep it.',
  ],
  welcomeMailNote:
    'We don’t send marketing emails. Your Stripe receipt is the only message you will get from us.',
  welcomeBack: 'Back to the homepage',

  callEyebrow: 'Talk to the team',
  callTitle: 'Book a 30-minute call with Stephen',
  callLead:
    'Ask us anything before you pay. We look at your profile together, and we tell you honestly whether NeverZero fits the way you post.',
  callDuration: '30 min · Google Meet',
  callCoversTitle: 'What we’ll cover',
  callCovers: [
    'How you post today, and what happens to a post in its first hours.',
    `How a wave works on your next post: real members, up to ${WAVE_REACTIONS} reactions and ${WAVE_COMMENTS} comments.`,
    'Whether NeverZero is right for you. If it isn’t, we’ll say so.',
  ],
  callSkip: 'Already sure?',
  callSkipLink: 'Start with your LinkedIn URL →',
  callPickTitle: 'Pick a time',
  callLoading: 'Loading available times…',
  callTimeZone: 'Times shown in your time zone:',
  callNoTimeWorks: 'None of these times work? Ask us to call you.',
  callChangeTime: 'Change time',
  callBackToTimes: 'Back to available times',
  callName: 'Your name',
  callEmail: 'Email',
  callLinkedIn: 'Your LinkedIn profile',
  callOptional: '(optional)',
  callFrequency: 'How often do you post?',
  callFrequencyOptions: {
    'not-yet': 'Not yet',
    monthly: 'A few times a month',
    weekly: 'About once a week',
    'several-per-week': 'Several times a week',
    daily: 'Every day',
  },
  callNote: 'Anything we should know?',
  callNotePlaceholder: 'Your goals, your audience, a question…',
  callBookSubmit: 'Book the call',
  callSending: 'Sending…',
  callErrorTaken: 'Someone just took that time. Please pick another one.',
  callErrorInvalid: 'Please check the fields above.',
  callErrorFailed: 'Something went wrong on our side. Please try again in a minute.',
  callBookedTitle: 'You’re booked.',
  callBookedLead: 'Stephen will send the calendar invite with the Meet link to',
  callCallbackTitle: 'Ask us to call you',
  callCallbackLead: 'Leave your number and we’ll find a time that works for you.',
  callCallbackOnlyLead: 'Leave your number and Stephen will call you back.',
  callPhone: 'Phone number, with country code',
  callCallbackNotePlaceholder: 'The best time to reach you, your time zone…',
  callCallbackSubmit: 'Request a call',
  callCallbackSentTitle: 'Got it.',
  callCallbackSentLead: 'Stephen will call you at the number you left.',

  whiteLabel: {
    navLabel: 'For agencies',
    seoTitle: 'White-label LinkedIn engagement for agencies',
    seoDescription: `Resell NeverZero to your clients under your brand. Up to ${WAVE_REACTIONS} reactions and ${WAVE_COMMENTS} comments on every post, ${NEVERZERO_PRICE_LABEL}/month excl. VAT per profile, nothing for your client to install.`,
    badge: 'For agencies & ghostwriters',
    h1a: 'Sell LinkedIn engagement to your clients.',
    h1b: 'Under your brand.',
    lead: `You manage LinkedIn for other people. Add NeverZero to your offer: every post your client publishes gets up to ${WAVE_REACTIONS} reactions and ${WAVE_COMMENTS} comments from real members. Your client sees the results. They never hear from us.`,
    checks: ['No access to your client’s account', 'Nothing for your client to install', 'One subscription per client, cancel anytime'],
    splitAgencyTitle: 'You',
    splitAgency: [
      'Paste the client’s profile URL',
      'Pay with your agency email',
      'Get the invoice in your name',
      'Set your own price',
    ],
    splitClientTitle: 'Your client',
    splitClient: [
      'Keeps posting as usual',
      'Gets engagement on every post',
      'Gives no password, installs nothing',
      'Never hears from NeverZero',
    ],
    howEyebrow: 'How it works',
    howH2: 'One profile, one subscription. All under your account.',
    steps: [
      {
        n: '1',
        title: 'Paste your client’s profile URL',
        body: 'Just the public LinkedIn URL. No password, no login, nothing for your client to do.',
      },
      {
        n: '2',
        title: 'Pay with your agency email',
        body: 'Use the same email for every client: each profile is its own monthly subscription under your account. Add your agency name and VAT number at checkout.',
      },
      {
        n: '3',
        title: 'Every post gets its audience',
        body: `Up to ${WAVE_REACTIONS} reactions and ${WAVE_COMMENTS} comments from real US-based members, spread over a few hours. Your client keeps posting as usual.`,
      },
    ],
    invisibleEyebrow: 'White label',
    invisibleH2: 'Your client never hears from us.',
    invisibleP: 'NeverZero works in the background. Nothing carries our name.',
    invisibleItems: [
      { title: 'No email to your client', body: 'We never contact the profile owner. Receipts and invoices go to you only.', icon: 'eye' },
      { title: 'No access to their account', body: 'All we use is the public profile URL. No password, no extension, nothing runs on their side.', icon: 'lock' },
      { title: 'Nothing on their posts', body: 'No tag, no link, no mention. The reactions and comments come from other members’ profiles.', icon: 'shield-check' },
      { title: 'Real members, US-based', body: 'Ordered from this English page, the engagement comes from US-based profiles. For French-speaking clients, order from the French page.', icon: 'users' },
    ],
    pricingEyebrow: 'Pricing',
    pricingH2: 'A fixed cost per client. The price you charge is yours.',
    pricingPer: '/month excl. VAT, per profile',
    pricingItems: [
      'Every original post, up to 100 a month',
      `Each one gets up to ${WAVE_REACTIONS} reactions and ${WAVE_COMMENTS} comments`,
      'No setup fee, no minimum term',
      'Cancel a client anytime from the link in that client’s receipt',
      'Satisfied or refunded within 30 days of your first payment',
    ],
    pricingMargin: `You decide what your client pays. Everything above ${NEVERZERO_PRICE_LABEL} is your margin.`,
    volumeTitle: 'Starting with several clients at once?',
    volumeBody: 'Book a call with Stephen before you add them. We’ll go through your client list together.',
    faqEyebrow: 'FAQ',
    faqH2: 'What agencies ask us.',
    faqs: [
      {
        q: 'Does my client need to do anything?',
        a: 'No. All we need is the public URL of their LinkedIn profile. No password, no login, no extension. They keep posting as usual.',
      },
      {
        q: 'Will my client know NeverZero is behind it?',
        a: 'Not from us. We never contact the profile owner, and nothing is added to their posts or their profile. The reactions and comments come from real members.',
      },
      {
        q: 'Can I manage all my clients from one account?',
        a: 'Yes. Use the same email at every checkout and each profile becomes its own subscription under your account. Each one has its own receipt, with the link to manage or cancel it.',
      },
      {
        q: 'Who gets the invoice?',
        a: 'You do. Enter your agency name and VAT number at checkout. Your client never receives anything from NeverZero.',
      },
      {
        q: 'What can I promise my client?',
        a: `Up to ${WAVE_REACTIONS} reactions and ${WAVE_COMMENTS} comments on each original post, spread over a few hours. We don’t guarantee impressions or reach: what LinkedIn does with a post afterwards depends on the content and the audience.`,
      },
      {
        q: 'My client already uses NeverZero or Podawaa. Can I add them?',
        a: 'A LinkedIn profile can only be on one account. If your client already has one with us, book a call before paying and we’ll sort it out with you.',
      },
      {
        q: 'What if a client stops working with me?',
        a: 'Cancel that profile’s subscription from its receipt. Your other clients are not affected.',
      },
    ],
    ctaH2: 'Add your first client today.',
    ctaP: 'Paste their LinkedIn profile URL and pay with your agency email. Their next post is covered.',
  },

  notFoundTitle: 'Page not found',
  notFoundBody: 'The page you’re looking for doesn’t exist, or has moved.',
  notFoundBack: 'Back to the homepage',
};

const fr: Dict = {
  navServices: 'Services',
  navMethod: 'Méthode',
  navTeam: 'Équipe',
  navPricing: 'Tarif',
  navBlog: 'Blog',
  navTools: 'Outils gratuits',
  protect: 'Travailler avec nous',
  callCta: 'Parler à l’équipe',

  heroBadge: 'Agence de croissance LinkedIn',
  heroH1a: 'On s’assure que vos posts',
  heroH1b: 'ne tombent plus jamais à zéro.',
  heroP1:
    'NeverZero est une agence LinkedIn fondée par Stephen et Tom. Vous publiez comme d’habitude. Notre équipe amène de vraies personnes sur chacun de vos posts.',
  redirecting: 'Redirection vers le paiement sécurisé…',
  urlInvalid: 'Ça ne ressemble pas à une URL de profil LinkedIn. Essayez linkedin.com/in/votre-profil.',
  checkoutFailed: 'Impossible d’ouvrir le paiement. Réessayez dans un instant.',

  formSubmit: 'Travailler avec nous',

  noAccess: 'Aucun mot de passe',
  realPeople: 'Vraies personnes, profils français uniquement',
  guarantee: 'Remboursé sous 30 jours',
  noCommitment: 'Sans engagement',

  servicesEyebrow: 'Ce qu’on prend en charge',
  services: [
    {
      title: 'On surveille votre profil',
      body: 'Quand vous publiez, on repère le nouveau post tout seuls. Rien à nous envoyer, rien à programmer.',
      icon: 'eye',
    },
    {
      title: 'De vraies personnes réagissent',
      body: `Jusqu’à ${WAVE_REACTIONS} réactions et ${WAVE_COMMENTS} commentaires sur chaque post, de vrais membres LinkedIn basés en France.`,
      icon: 'users',
    },
    {
      title: 'Au rythme d’une vraie audience',
      body: 'Les réactions arrivent sur quelques heures, jamais d’un coup. On ne touche jamais à votre compte.',
      icon: 'clock',
    },
  ],

  journeyH2: 'Du post à l’opportunité',
  journeyP:
    'Chaque étape nourrit la suivante : un post qui démarre au-dessus de zéro est lu, reçoit des réponses et atteint les personnes pour qui vous l’avez écrit.',
  journeySteps: [
    { title: 'Crédibilité', body: 'Un post qui a déjà des réactions donne envie d’être lu.' },
    { title: 'Portée', body: 'Les premières interactions donnent à LinkedIn une raison de le montrer plus loin.' },
    { title: 'Conversations', body: 'Des commentaires écrits pour votre post invitent les autres à s’y joindre.' },
    { title: 'Opportunités', body: 'Vos lancements, recrutements et succès atteignent ceux à qui ils s’adressent.' },
  ],
  journeyImageAlt: 'Deux personnes qui travaillent ensemble sur un ordinateur portable',

  previewToast: 'Nouveau post repéré, boost lancé',
  previewToastTime: 'À l’instant',
  previewProgress: 'Boost en cours',
  previewReactions: 'Réactions',
  previewComments: 'Commentaires',
  previewNote: 'De vraies personnes, sur quelques heures.',

  quote:
    '« LinkedIn est le meilleur endroit pour construire en public. Notre rôle : faire en sorte qu’aucun de vos posts ne soit ignoré. »',
  quoteAuthor: 'L’équipe NeverZero',

  trustedBy: 'Aux côtés des outils que vous utilisez déjà',
  clientsTrust: 'Ils nous font confiance',

  focusEyebrow: 'Les posts business d’abord, tous vos posts couverts',
  focusH2: 'Chaque post démarre au-dessus de zéro.',
  focusP:
    `Un lancement bloqué à ${BEFORE_REACTIONS} likes ne rate pas seulement sa portée. Il dit à tous ceux qui passent que personne n’a réagi. Vos lancements, vos recrutements et vos succès clients sont les posts qui font tourner votre activité, et ceux qui ne peuvent pas se permettre un départ à plat. NeverZero couvre chaque post que vous publiez, pour que ceux-là ne tombent jamais dans le vide.`,
  focusItems: [
    { title: 'Lancements produit', body: '« Obtenez l’attention que votre lancement mérite. »', icon: 'rocket' },
    { title: 'Succès clients', body: '« Transformez vos réussites en opportunités. »', icon: 'trophy' },
    { title: 'Recrutement', body: '« Touchez les bons talents, plus vite. »', icon: 'user-plus' },
    { title: 'Et tout le reste', body: '« Opinions, actus, histoires perso. Tout démarre au-dessus de zéro. »', icon: 'sparkles' },
  ],

  promiseH2a: 'On ne promet pas la viralité.',
  promiseH2b: 'On s’assure que vous ne partez pas de zéro.',
  promiseP:
    'Quand quelqu’un arrive sur votre post, il ne tombe pas sur un désert. Les premières réactions et les premiers commentaires sont déjà là. C’est tout notre travail. Ensuite, on se retire et votre contenu fait le reste.',

  compareEyebrow: 'Face aux alternatives',
  compareH2: 'Pourquoi pas un pod, ou des likes achetés ?',
  compareCols: ['NeverZero', 'Pods d’engagement', 'Likes achetés', 'Ne rien faire'],
  compareRows: [
    { label: 'Votre temps', cells: ['Aucun, vous publiez comme d’habitude', 'Réagir aux autres, tous les jours', 'Aucun', 'Aucun'] },
    { label: 'Qui réagit', cells: ['De vrais membres LinkedIn basés en France', 'Les membres du groupe, quel que soit leur secteur', 'Souvent des bots ou des profils vides', 'Ceux qui tombent dessus'] },
    { label: 'Commentaires', cells: ['Écrits pour votre post, dans sa langue', 'Souvent génériques', 'Rarement', 'Souvent aucun'] },
    { label: 'Comment ça arrive', cells: ['Réparti sur quelques heures', 'Quand le groupe se connecte', 'Souvent en un seul pic', 'Souvent, ça n’arrive pas'] },
    { label: 'Coût', cells: [`${NEVERZERO_PRICE_LABEL}/mois HT`, 'Gratuit, payé en temps', 'Bon marché au like', 'Gratuit'] },
  ],

  howEyebrow: 'Notre méthode',
  howH2: 'Vous publiez. On s’occupe du reste.',
  methodVideoLabel:
    'Notre méthode en 30 secondes : vous nous envoyez votre profil LinkedIn, on prend le relais, et chaque post trouve son audience.',
  steps: [
    {
      n: '1',
      title: 'Vous nous envoyez votre profil LinkedIn',
      body: 'Juste l’URL publique, puis le paiement. On ne vous demande jamais de mot de passe.',
    },
    {
      n: '2',
      title: 'On prend le relais',
      body: 'Votre profil est configuré de notre côté. Vous publiez comme d’habitude.',
    },
    {
      n: '3',
      title: 'Chaque post trouve son audience',
      body: `Notre réseau apporte jusqu’à ${WAVE_REACTIONS} réactions et ${WAVE_COMMENTS} commentaires de vrais membres basés en France.`,
    },
  ],

  riskEyebrow: 'Zéro risque',
  riskH2: 'Pas convaincu ? Remboursé.',
  riskP: 'Vous n’avez rien à perdre.',
  riskItems: [
    { title: 'Remboursé sous 30 jours', body: 'Répondez à votre reçu. On rembourse votre premier mois.', icon: 'rotate-ccw' },
    { title: 'Aucun mot de passe', body: 'On ne touche jamais à votre compte.', icon: 'lock' },
    { title: 'Sans engagement', body: 'Au mois. Résiliable à tout moment.', icon: 'calendar-x' },
  ],

  trustEyebrow: 'Conçu pour la confiance',
  trustH2: 'Votre compte reste intact.',
  trustP:
    'LinkedIn peut-il pénaliser mon compte ? C’est la question que tout le monde pose. On ne s’y connecte jamais et rien ne tourne de votre côté. Ni mot de passe, ni extension, ni automatisation. On n’utilise que l’URL de votre profil public, et les réactions et les commentaires viennent des profils d’autres membres.',
  trustItems: [
    'Aucun accès à votre compte, rien n’y tourne',
    'Ni mot de passe, ni extension à installer',
    'De vrais membres LinkedIn, uniquement des profils français',
    'Des petites vagues sur quelques heures, jamais de pic',
    'Rien n’est ajouté à vos posts ni à votre profil',
    'Au mois, résiliable à tout moment',
    'Pas satisfait les 30 premiers jours ? Remboursé en totalité',
  ],

  teamEyebrow: 'L’équipe',
  joinH2: 'Les gens derrière vos posts.',
  joinP:
    'On a créé NeverZero parce qu’on avait le même problème. Nos histoires perso faisaient réagir. Nos lancements, nos recrutements et nos actus d’entreprise, souvent non. Alors on a construit un filet de sécurité simple pour chaque post qui compte.',
  joinCount: 'professionnels LinkedIn dans la communauté derrière chaque boost',

  testiEyebrow: 'Ce que disent les fondateurs',
  testiH2: 'Ils en parlent.',
  testiBased: 'sur {count} avis de fondateurs',
  testiStars: 'Noté {count} sur 5',

  pricingEyebrow: 'Prix',
  pricingH2: 'Un forfait mensuel. Tous vos posts couverts.',
  pricingPer: '/mois HT',
  pricingItems: [
    'Chaque post original que vous publiez, jusqu’à 100 par mois',
    `Chacun reçoit jusqu’à ${WAVE_REACTIONS} réactions et ${WAVE_COMMENTS} commentaires`,
    'Pas de frais d’installation, pas de durée minimale',
    'Résiliable à tout moment depuis le lien de votre reçu',
    'Satisfait ou remboursé, avec un remboursement intégral dans les 30 jours suivant votre premier paiement',
  ],
  // Trois posts par semaine ≈ 13 posts par mois.
  pricingMath: `Vous publiez trois fois par semaine ? Ça revient à environ ${Math.round(NEVERZERO_PRICE_AMOUNT / 13)} € par post.`,

  faqH2: 'Questions fréquentes.',
  faqSeeAll: 'Voir les {count} questions',
  faqPageTitle: 'Tout ce que vous voulez savoir.',
  faqPageIntro:
    'Comment fonctionne NeverZero, qui interagit avec vos posts, quels posts sont couverts, et ce qui se passe si vous résiliez.',
  faqGroups: [
    {
      title: 'Comment fonctionne NeverZero',
      items: [
        {
          top: 1,
          q: 'Que fait exactement NeverZero ?',
          a: `Chaque post LinkedIn que vous publiez reçoit une première vague d’engagement (jusqu’à ${WAVE_REACTIONS} réactions et ${WAVE_COMMENTS} commentaires) pour ne jamais démarrer à zéro. Vous nous donnez votre profil LinkedIn, vous continuez à publier comme d’habitude, et un membre de notre équipe s’occupe du reste.`,
        },
        {
          q: 'Comment NeverZero détecte-t-il mes nouveaux posts ?',
          a: 'On consulte votre profil LinkedIn public à la recherche de nouveaux posts : toutes les quelques minutes en journée de travail, au moins toutes les deux heures en dehors. Vous n’avez jamais de post à soumettre.',
        },
        {
          q: 'Dois-je installer quelque chose ?',
          a: 'Non. Pas d’extension de navigateur, pas d’application de bureau ni mobile.',
        },
        {
          top: 4,
          q: 'Avez-vous besoin d’accéder à mon compte LinkedIn ?',
          a: 'Non. Il nous suffit de l’URL de votre profil LinkedIn public. Pas de mot de passe, pas de connexion à votre compte. Rien ne tourne jamais dessus.',
        },
        {
          q: 'Dois-je vous envoyer chaque post ?',
          a: 'Non. Dès que votre abonnement est actif, chaque nouveau post de votre profil est pris en charge automatiquement.',
        },
        {
          q: 'Que se passe-t-il après ma publication ?',
          a: 'Dès qu’on détecte le post, sa première vague démarre : quelques réactions dans les premières minutes, puis le reste réparti sur les heures qui suivent, avec les commentaires au fil de l’eau.',
        },
        {
          top: 6,
          q: 'En combien de temps l’engagement démarre-t-il ?',
          a: 'Dès qu’on détecte votre post. La plupart du temps, c’est dans les minutes qui suivent sa publication ; la nuit et le week-end, cela peut prendre jusqu’à deux heures environ.',
        },
        {
          q: 'Quand NeverZero s’arrête-t-il ?',
          a: 'Une fois la première vague livrée, en général dans les quelques heures qui suivent la publication. On n’ajoute plus rien au post ensuite.',
        },
      ],
    },
    {
      title: 'Qui interagit avec mes posts ?',
      items: [
        {
          top: 5,
          q: 'Quels profils interagissent avec mes posts ?',
          a: 'Des fondateurs, opérateurs et professionnels de votre écosystème : des personnes qui auraient pu réagir à votre contenu de toute façon. Côté francophone, notre réseau est exclusivement français : tous les profils qui interagissent avec vos posts sont en France.',
        },
        {
          q: 'Ce sont de vrais profils LinkedIn ?',
          a: 'Oui. Chaque réaction et chaque commentaire vient du profil d’un vrai membre LinkedIn de notre réseau. Aucun faux profil, aucun profil généré.',
        },
        {
          q: 'Les profils sont-ils basés en France ?',
          a: 'Oui. Côté francophone, tous les profils qui interagissent avec vos posts sont basés en France.',
        },
        {
          q: 'Puis-je choisir qui interagit avec mes posts ?',
          a: 'Non. Vous ne choisissez pas les profils : l’appariement est automatique, il n’y a rien à gérer.',
        },
        {
          q: 'Puis-je choisir des secteurs ou des intitulés de poste ?',
          a: 'Non. Les profils sont choisis automatiquement selon le sujet de vos posts, mais on ne propose pas de ciblage par secteur ou par poste.',
        },
        {
          q: 'Est-ce que ce sont toujours les mêmes personnes ?',
          a: 'Pas de groupe fixe. Chaque vague est tirée de notre réseau avec une part d’aléatoire : les profils changent d’un post à l’autre.',
        },
        {
          q: 'Quels types de réactions puis-je recevoir ?',
          a: 'Un mélange de réactions LinkedIn (J’aime, Instructif, Bravo, Soutien et J’adore) adapté au ton du post, plus des commentaires. Une annonce difficile ne recevra pas de « Bravo ».',
        },
        {
          q: 'Les commentaires sont-ils en rapport avec mon post ?',
          a: 'Oui. Chaque commentaire est écrit pour votre post, à partir de ce qu’il dit vraiment, dans la langue où il est rédigé, pas une formule passe-partout. Un post sans aucun texte reçoit seulement des réactions, faute de matière à commenter.',
        },
      ],
    },
    {
      title: 'Est-ce que ça aura l’air naturel ?',
      items: [
        {
          top: 3,
          q: 'Est-ce que ça ressemblera à du faux engagement ?',
          a: 'Non. Les réactions et commentaires viennent de profils LinkedIn français réels et pertinents, et arrivent progressivement sur quelques heures, en petit volume. Une première vague naturelle, pas un pic.',
        },
        {
          top: 2,
          q: 'LinkedIn peut-il pénaliser mon compte ?',
          a: 'On ne se connecte jamais à votre compte et rien n’y tourne : ni mot de passe, ni extension, ni automatisation de votre côté. On n’utilise que l’URL de votre profil public. Les réactions et les commentaires viennent des profils d’autres membres, en petites vagues réparties sur quelques heures plutôt qu’en un pic soudain.',
        },
        {
          q: 'Envoyez-vous des centaines de likes d’un coup ?',
          a: `Non. Jusqu’à ${WAVE_REACTIONS} réactions par post, réparties sur plusieurs heures. Un pic soudain, c’est précisément ce qu’on évite.`,
        },
        {
          q: 'Chaque post reçoit-il exactement le même volume ?',
          a: 'Non. La taille de la vague varie un peu d’un post à l’autre, tout comme le mélange des réactions.',
        },
        {
          top: 10,
          q: 'Garantissez-vous un nombre précis de likes ou de commentaires ?',
          a: `Chaque vague est calibrée jusqu’à ${WAVE_REACTIONS} réactions et ${WAVE_COMMENTS} commentaires, mais c’est une première vague, pas un compteur : les chiffres exacts varient d’un post à l’autre, et on ne promet pas un total identique à chaque fois.`,
        },
        {
          q: 'Garantissez-vous plus d’impressions ou de portée ?',
          a: 'Non. On livre le premier engagement. Ce que LinkedIn fait ensuite de votre post (impressions, portée, leads, ventes) dépend de votre contenu et de votre audience, et personne ne peut le garantir.',
        },
        {
          q: 'NeverZero peut-il rendre un post viral ?',
          a: 'Ce n’est pas la promesse. On s’assure que votre post ne démarre pas à zéro. La suite dépend du post.',
        },
        {
          q: 'Peut-on voir que j’utilise NeverZero ?',
          a: 'Rien n’est ajouté à vos posts ni à votre profil : pas de badge, pas de mention, pas de message. On voit seulement des gens qui réagissent et qui commentent.',
        },
      ],
    },
    {
      title: 'Quels posts sont couverts ?',
      items: [
        {
          top: 8,
          q: 'Quels posts sont couverts ?',
          a: 'Chaque post original que vous publiez pendant votre abonnement : lancements, recrutement, succès clients, études de cas, opinions, histoires perso. Les republications du contenu d’autres personnes ne sont pas boostées, pas plus que les posts publiés avant votre abonnement.',
        },
        {
          q: 'Y a-t-il une limite au nombre de posts couverts ?',
          a: 'Jusqu’à 100 posts par mois, sans limite par jour. Bien plus que ce que quiconque publie.',
        },
        {
          q: 'Puis-je exclure un post ?',
          a: 'Oui, mais pas depuis un bouton : il n’y a pas de tableau de bord à gérer. Envoyez-nous le lien du post, via les coordonnées indiquées sur votre reçu, et on le retire de la file. Les posts sont pris en charge vite : prévenez-nous dès sa publication.',
        },
        {
          q: 'Et si je publie plusieurs fois dans la journée ?',
          a: 'Chaque post est détecté et reçoit sa propre première vague. Il n’y a pas de limite par jour.',
        },
        {
          q: 'NeverZero fonctionne-t-il avec les posts texte ?',
          a: 'Oui.',
        },
        {
          q: 'Et avec les posts image, carrousel et vidéo ?',
          a: 'Oui. Tout post original de votre profil est couvert, quel que soit son format. Un post sans aucun texte reçoit des réactions mais pas de commentaires.',
        },
        {
          q: 'Et avec les newsletters ou les articles LinkedIn ?',
          a: 'NeverZero est conçu pour les posts classiques du fil. La prise en charge des articles et newsletters LinkedIn n’est pas garantie.',
        },
      ],
    },
    {
      title: 'Compte, confidentialité et mise en place',
      items: [
        {
          q: 'Avez-vous besoin de mon mot de passe LinkedIn ?',
          a: 'Non. Jamais.',
        },
        {
          q: 'NeverZero peut-il publier depuis mon compte ?',
          a: 'Non. On n’a aucun accès à votre compte, on ne peut donc rien y publier.',
        },
        {
          q: 'NeverZero peut-il envoyer des messages depuis mon compte ?',
          a: 'Non. Même raison : aucun accès, aucun message.',
        },
        {
          q: 'NeverZero peut-il modifier ou supprimer mes posts ?',
          a: 'Non. On ne peut pas toucher à vos posts. Les réactions et commentaires viennent d’autres profils.',
        },
        {
          q: 'Dois-je vous donner un accès administrateur à quoi que ce soit ?',
          a: 'Non. L’URL de votre profil LinkedIn public suffit.',
        },
        {
          q: 'Puis-je utiliser NeverZero sur une page entreprise ?',
          a: 'Pas aujourd’hui. NeverZero fonctionne uniquement sur les profils LinkedIn personnels : l’inscription demande une URL de profil linkedin.com/in/, pas une page entreprise.',
        },
      ],
    },
    {
      title: 'Prix, résiliation et résultats',
      items: [
        {
          q: 'Combien coûte NeverZero ?',
          a: `${NEVERZERO_PRICE_LABEL} par mois HT, pour tous les posts que vous publiez. Pas de frais d’installation.`,
        },
        {
          top: 7,
          q: 'Et si je ne suis pas satisfait ?',
          a: 'Vous êtes remboursé. Si NeverZero n’est pas pour vous, demandez-le dans les 30 jours suivant votre premier paiement en répondant à votre reçu, et on vous rembourse ce premier mois en totalité. Sans justification.',
        },
        {
          q: 'Y a-t-il un engagement de durée ?',
          a: 'Non. C’est un abonnement mensuel sans durée minimale. Résiliable à tout moment depuis le lien de votre reçu.',
        },
        {
          top: 9,
          q: 'Je publie peu, ça vaut le coup ?',
          a: `Le prix est le même que vous publiiez deux ou vingt fois : ${NEVERZERO_PRICE_LABEL} par mois. Si vous publiez une ou deux fois par mois, ça fait 100 € ou plus par post, et ça ne vaut probablement pas le coup. À partir d’environ un post par semaine, chaque post qui compte (un lancement, un recrutement, un succès client) démarre devant un public plutôt que dans le silence.`,
        },
        {
          q: 'Que se passe-t-il si je résilie ?',
          a: 'Vos posts restent couverts jusqu’à la fin du mois payé, puis on s’arrête. Les posts qui ont déjà reçu leur première vague gardent leurs réactions et leurs commentaires.',
        },
        {
          q: 'À qui s’adresse NeverZero ?',
          a: 'Aux fondateurs, opérateurs, consultants et professionnels qui publient sur LinkedIn pour leur activité et ne veulent plus voir leurs posts importants démarrer à zéro.',
        },
      ],
    },
  ],

  seoTitle: 'Agence de croissance LinkedIn. Ne postez plus jamais à zéro.',
  ctaH2: 'Envoyez-nous votre profil. On prend le relais.',
  ctaP: 'Notre équipe couvre chaque post que vous publiez, dès le prochain.',
  ctaNote: `${NEVERZERO_PRICE_LABEL}/mois HT · Remboursé sous 30 jours · Sans engagement`,

  privacy: 'Confidentialité',
  terms: 'CGU',
  extensionsPrivacy: 'Confidentialité des extensions',
  extensionsTerms: 'Conditions des extensions',
  footerTag: 'Pour un monde professionnel plus ouvert.',

  blogTitle: 'Le blog NeverZero',
  blogIntro: 'Notes sur la portée LinkedIn, la distribution et le build in public, écrites pour les fondateurs.',
  blogBack: '← Tous les articles',
  blogEmpty: 'Pas encore d’articles. Revenez bientôt.',
  blogReadMore: 'Lire l’article',
  blogMinutes: 'min de lecture',
  blogCtaH2: 'Ne postez plus jamais à zéro.',
  blogCtaP: 'Donnez à chaque post que vous publiez la première vague d’engagement dont il a besoin.',

  toolsPageTitle: 'Outils gratuits LinkedIn',
  toolsPageIntro: 'Des diagnostics rapides pour vos posts LinkedIn, sans compte ni mot de passe.',
  toolsEmpty: 'Pas encore d’outil dans cette langue. Revenez bientôt.',
  toolsTry: 'Essayer l’outil',

  welcomeTitle: 'C’est en place.',
  welcomeLead:
    'Votre abonnement est actif. Un membre de notre équipe prend le relais : à partir de maintenant, chaque post que vous publiez sur LinkedIn reçoit sa première vague d’engagement.',
  welcomeSteps: [
    'Rien à installer, rien à connecter : nous avons déjà votre URL LinkedIn publique.',
    'Votre prochain post est pris en charge tout seul, en général dans les minutes qui suivent sa publication.',
    'Votre reçu contient le lien pour gérer ou résilier votre abonnement. Conservez-le.',
  ],
  welcomeMailNote:
    'Nous n’envoyons aucun email marketing. Votre reçu Stripe est le seul message que vous recevrez de notre part.',
  welcomeBack: 'Retour à l’accueil',

  callEyebrow: 'Parler à l’équipe',
  callTitle: 'Réservez 30 minutes avec Stephen',
  callLead:
    'Posez-nous vos questions avant de payer. On regarde votre profil ensemble, et on vous dit franchement si NeverZero colle à votre façon de publier.',
  callDuration: '30 min · Google Meet',
  callCoversTitle: 'Ce qu’on verra ensemble',
  callCovers: [
    'Comment vous publiez aujourd’hui, et ce que devient un post dans ses premières heures.',
    `Comment se passe une vague sur votre prochain post : de vrais membres, jusqu’à ${WAVE_REACTIONS} réactions et ${WAVE_COMMENTS} commentaires.`,
    'Si NeverZero est fait pour vous. Si ce n’est pas le cas, on vous le dira.',
  ],
  callSkip: 'Déjà convaincu ?',
  callSkipLink: 'Commencer avec votre URL LinkedIn →',
  callPickTitle: 'Choisissez un créneau',
  callLoading: 'Chargement des créneaux…',
  callTimeZone: 'Heures affichées dans votre fuseau :',
  callNoTimeWorks: 'Aucun créneau ne vous convient ? Demandez à être rappelé.',
  callChangeTime: 'Changer de créneau',
  callBackToTimes: 'Revenir aux créneaux',
  callName: 'Votre nom',
  callEmail: 'Email',
  callLinkedIn: 'Votre profil LinkedIn',
  callOptional: '(facultatif)',
  callFrequency: 'À quelle fréquence publiez-vous ?',
  callFrequencyOptions: {
    'not-yet': 'Pas encore',
    monthly: 'Quelques fois par mois',
    weekly: 'Environ une fois par semaine',
    'several-per-week': 'Plusieurs fois par semaine',
    daily: 'Tous les jours',
  },
  callNote: 'Quelque chose à nous dire ?',
  callNotePlaceholder: 'Vos objectifs, votre audience, une question…',
  callBookSubmit: 'Réserver l’appel',
  callSending: 'Envoi…',
  callErrorTaken: 'Ce créneau vient d’être pris. Choisissez-en un autre.',
  callErrorInvalid: 'Vérifiez les champs ci-dessus.',
  callErrorFailed: 'Un problème de notre côté. Réessayez dans une minute.',
  callBookedTitle: 'C’est réservé.',
  callBookedLead: 'Stephen vous envoie l’invitation avec le lien Meet à',
  callCallbackTitle: 'Demander à être rappelé',
  callCallbackLead: 'Laissez votre numéro, on trouvera un moment qui vous convient.',
  callCallbackOnlyLead: 'Laissez votre numéro, Stephen vous rappelle.',
  callPhone: 'Téléphone, avec l’indicatif du pays',
  callCallbackNotePlaceholder: 'Le meilleur moment pour vous joindre, votre fuseau…',
  callCallbackSubmit: 'Être rappelé',
  callCallbackSentTitle: 'C’est noté.',
  callCallbackSentLead: 'Stephen vous appellera au numéro que vous avez laissé.',

  whiteLabel: {
    navLabel: 'Agences',
    seoTitle: 'Engagement LinkedIn en marque blanche pour les agences',
    seoDescription: `Revendez NeverZero à vos clients sous votre marque. Jusqu’à ${WAVE_REACTIONS} réactions et ${WAVE_COMMENTS} commentaires sur chaque post, ${NEVERZERO_PRICE_LABEL}/mois HT par profil, rien à installer pour votre client.`,
    badge: 'Pour les agences et ghostwriters',
    h1a: 'Vendez de l’engagement LinkedIn à vos clients.',
    h1b: 'Sous votre marque.',
    lead: `Vous gérez LinkedIn pour d’autres. Ajoutez NeverZero à votre offre : chaque post de votre client reçoit jusqu’à ${WAVE_REACTIONS} réactions et ${WAVE_COMMENTS} commentaires de vrais membres. Votre client voit les résultats. Il n’entend jamais parler de nous.`,
    checks: ['Aucun accès au compte de votre client', 'Rien à installer pour votre client', 'Un abonnement par client, sans engagement'],
    splitAgencyTitle: 'Vous',
    splitAgency: [
      'Collez l’URL du profil client',
      'Payez avec l’email de l’agence',
      'Recevez la facture à votre nom',
      'Fixez votre propre prix',
    ],
    splitClientTitle: 'Votre client',
    splitClient: [
      'Continue de publier comme d’habitude',
      'Reçoit de l’engagement sur chaque post',
      'Ne donne aucun mot de passe, n’installe rien',
      'N’entend jamais parler de NeverZero',
    ],
    howEyebrow: 'Comment ça marche',
    howH2: 'Un profil, un abonnement. Tout sur votre compte.',
    steps: [
      {
        n: '1',
        title: 'Collez l’URL du profil de votre client',
        body: 'Juste l’URL publique LinkedIn. Ni mot de passe ni connexion : votre client n’a rien à faire.',
      },
      {
        n: '2',
        title: 'Payez avec l’email de votre agence',
        body: 'Le même email pour tous vos clients : chaque profil devient un abonnement mensuel distinct sur votre compte. Indiquez le nom de l’agence et votre numéro de TVA au paiement.',
      },
      {
        n: '3',
        title: 'Chaque post trouve son public',
        body: `Jusqu’à ${WAVE_REACTIONS} réactions et ${WAVE_COMMENTS} commentaires de vrais membres basés en France, répartis sur quelques heures. Votre client publie comme d’habitude.`,
      },
    ],
    invisibleEyebrow: 'Marque blanche',
    invisibleH2: 'Votre client n’entend jamais parler de nous.',
    invisibleP: 'NeverZero travaille en coulisses. Rien ne porte notre nom.',
    invisibleItems: [
      { title: 'Aucun email à votre client', body: 'On ne contacte jamais le titulaire du profil. Reçus et factures arrivent chez vous seulement.', icon: 'eye' },
      { title: 'Aucun accès à son compte', body: 'On utilise seulement l’URL publique du profil. Pas de mot de passe, pas d’extension, rien ne tourne de son côté.', icon: 'lock' },
      { title: 'Rien sur ses posts', body: 'Pas de tag, pas de lien, pas de mention. Les réactions et commentaires viennent des profils d’autres membres.', icon: 'shield-check' },
      { title: 'De vrais membres, basés en France', body: 'Commandé depuis cette page en français, l’engagement vient de profils français. Pour un client anglophone, commandez depuis la page en anglais.', icon: 'users' },
    ],
    pricingEyebrow: 'Prix',
    pricingH2: 'Un coût fixe par client. Le prix que vous facturez vous appartient.',
    pricingPer: '/mois HT, par profil',
    pricingItems: [
      'Chaque post original, jusqu’à 100 par mois',
      `Chacun reçoit jusqu’à ${WAVE_REACTIONS} réactions et ${WAVE_COMMENTS} commentaires`,
      'Pas de frais de mise en place, pas de durée minimale',
      'Arrêtez un client quand vous voulez, depuis le lien de son reçu',
      'Satisfait ou remboursé dans les 30 jours suivant votre premier paiement',
    ],
    pricingMargin: `Vous fixez ce que paie votre client. Tout ce qui dépasse ${NEVERZERO_PRICE_LABEL} est votre marge.`,
    volumeTitle: 'Vous démarrez avec plusieurs clients ?',
    volumeBody: 'Réservez un appel avec Stephen avant de les ajouter. On passe votre liste de clients en revue ensemble.',
    faqEyebrow: 'FAQ',
    faqH2: 'Ce que les agences nous demandent.',
    faqs: [
      {
        q: 'Mon client doit-il faire quelque chose ?',
        a: 'Non. Il nous faut seulement l’URL publique de son profil LinkedIn. Ni mot de passe, ni connexion, ni extension. Il continue de publier comme d’habitude.',
      },
      {
        q: 'Mon client saura-t-il que NeverZero est derrière ?',
        a: 'Pas par nous. On ne contacte jamais le titulaire du profil, et rien n’est ajouté à ses posts ni à son profil. Les réactions et commentaires viennent de vrais membres.',
      },
      {
        q: 'Puis-je gérer tous mes clients depuis un seul compte ?',
        a: 'Oui. Utilisez le même email à chaque paiement : chaque profil devient un abonnement distinct sur votre compte. Chacun a son propre reçu, avec le lien pour le gérer ou l’annuler.',
      },
      {
        q: 'Qui reçoit la facture ?',
        a: 'Vous. Saisissez le nom de votre agence et votre numéro de TVA au paiement. Votre client ne reçoit jamais rien de NeverZero.',
      },
      {
        q: 'Que puis-je promettre à mon client ?',
        a: `Jusqu’à ${WAVE_REACTIONS} réactions et ${WAVE_COMMENTS} commentaires sur chaque post original, répartis sur quelques heures. On ne garantit ni impressions ni portée : ce que LinkedIn fait d’un post ensuite dépend du contenu et de l’audience.`,
      },
      {
        q: 'Mon client utilise déjà NeverZero ou Podawaa. Puis-je l’ajouter ?',
        a: 'Un profil LinkedIn ne peut être que sur un seul compte. Si votre client en a déjà un chez nous, réservez un appel avant de payer et on règle ça avec vous.',
      },
      {
        q: 'Et si un client arrête de travailler avec moi ?',
        a: 'Annulez l’abonnement de ce profil depuis son reçu. Vos autres clients ne sont pas concernés.',
      },
    ],
    ctaH2: 'Ajoutez votre premier client aujourd’hui.',
    ctaP: 'Collez l’URL de son profil LinkedIn et payez avec l’email de votre agence. Son prochain post est couvert.',
  },

  notFoundTitle: 'Page introuvable',
  notFoundBody: 'La page que vous cherchez n’existe pas, ou a été déplacée.',
  notFoundBack: 'Retour à l’accueil',
};

export const I18N: Record<Lang, Dict> = { en, fr };

export function isLang(value: unknown): value is Lang {
  return value === 'en' || value === 'fr';
}

export function dictFor(lang: Lang): Dict {
  return I18N[lang] ?? I18N[DEFAULT_LANG];
}
